import type { BarcodeFormat, BulkBarcodeFormatResult } from '@erp/shared';
import { ScanLine } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

/** QR is deliberately absent: it isn't a retail product symbology. */
const FORMATS: readonly BarcodeFormat[] = ['ean13', 'code128', 'ean8', 'upc_a'];

export interface BarcodeFormatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a real (non-dry) run, so the list can refetch. */
  onApplied?: () => void;
}

/**
 * Set the barcode symbology on the WHOLE catalogue — the one-shot a shop needs after
 * an import, where every product landed on the Code 128 default while the supplier
 * codes are really EAN-13.
 *
 * The dialog opens on a DRY RUN and re-runs it on every change, so the owner reads
 * "2,338 of 2,589 — 251 skipped" BEFORE committing rather than after. That matters
 * because the skipped ones are not a rounding error: this only changes metadata, and
 * a barcode the chosen symbology cannot carry would simply make the label fall back
 * to Code 128 while claiming to be an EAN-13.
 */
export function BarcodeFormatDialog({ open, onOpenChange, onApplied }: BarcodeFormatDialogProps): JSX.Element {
  const { t } = useTranslation('items');
  const { t: tc } = useTranslation();
  const [format, setFormat] = useState<BarcodeFormat>('ean13');
  const [onlyValid, setOnlyValid] = useState(true);
  const [preview, setPreview] = useState<BulkBarcodeFormatResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  const run = useCallback(async (dryRun: boolean): Promise<BulkBarcodeFormatResult | null> => {
    const r = await api().post<BulkBarcodeFormatResult>('/items/bulk/barcode-format', { format, onlyValid, dryRun });
    return r.data;
  }, [format, onlyValid]);

  // Re-count whenever the choice changes. `live` guards the usual out-of-order
  // response: flipping the checkbox twice quickly must not leave the slower answer
  // on screen next to the newer settings.
  useEffect(() => {
    if (!open) { setPreview(null); return; }
    let live = true;
    setBusy(true);
    void run(true)
      .then((d) => { if (live) setPreview(d); })
      .catch(() => { if (live) setPreview(null); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [open, run]);

  const apply = async (): Promise<void> => {
    setApplying(true);
    try {
      const d = await run(false);
      toast.success(t('barcodeBulk.done', { defaultValue: 'Changed {{n}} product(s) to {{format}}.', n: d?.updated ?? 0, format: t(`barcodeFormat.${format}`) }));
      onApplied?.();
      onOpenChange(false);
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setApplying(false);
    }
  };

  const skipped = preview?.skipped ?? 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-4 w-4" aria-hidden />
            {t('barcodeBulk.title', { defaultValue: 'Barcode type for all products' })}
          </DialogTitle>
          <DialogDescription>
            {t('barcodeBulk.description', { defaultValue: 'Sets which barcode symbology every product is printed in. The barcode numbers themselves are never changed.' })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex items-center justify-between gap-2 text-sm">
            <span className="text-fg-muted">{t('create.barcodeFormat')}</span>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value as BarcodeFormat)}
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
            >
              {FORMATS.map((f) => <option key={f} value={f}>{t(`barcodeFormat.${f}`)}</option>)}
            </select>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-brand" checked={onlyValid} onChange={(e) => setOnlyValid(e.target.checked)} />
            <span>
              {t('barcodeBulk.onlyValid', { defaultValue: 'Only products whose barcode already fits this type' })}
              <span className="block text-[11px] text-fg-subtle">
                {t('barcodeBulk.onlyValidHint', { defaultValue: 'Recommended. A barcode that doesn’t fit still prints — as Code 128 — so the label would claim a type it isn’t.' })}
              </span>
            </span>
          </label>

          {/* The counts, before committing. */}
          <div className="rounded-md border border-dashed border-border px-3 py-2 text-sm">
            {busy || !preview ? (
              <span className="text-fg-muted">{tc('common.loading', { defaultValue: 'Loading…' })}</span>
            ) : (
              <>
                <p>
                  {t('barcodeBulk.preview', {
                    defaultValue: '{{matched}} of {{total}} products will be set to {{format}}.',
                    matched: preview.matched, total: preview.total, format: t(`barcodeFormat.${format}`),
                  })}
                </p>
                {/* The dangerous half named on its own: a renumbered label scans
                    perfectly and finds nothing at the till. */}
                {preview.renumbered > 0 && (
                  <p className={`mt-1 text-[11px] ${onlyValid ? 'text-fg-subtle' : 'text-danger'}`}>
                    {t('barcodeBulk.renumbered', {
                      defaultValue: '{{n}} product(s) are one digit short, so their label would print a DIFFERENT barcode and scan to nothing.',
                      n: preview.renumbered,
                    })}
                  </p>
                )}
                {preview.unsupported > 0 && (
                  <p className="mt-1 text-[11px] text-fg-subtle">
                    {t('barcodeBulk.unsupported', {
                      defaultValue: '{{n}} product(s) can’t be this type at all — their label falls back to Code 128 with the right number on it.',
                      n: preview.unsupported,
                    })}
                  </p>
                )}
                {skipped > 0 && (
                  <p className="mt-1 text-[11px] text-warning">
                    {t('barcodeBulk.skipped', { defaultValue: '{{n}} skipped, and left on their current type.', n: skipped })}
                  </p>
                )}
                {!onlyValid && preview.renumbered + preview.unsupported > 0 && (
                  <p className="mt-1 text-[11px] text-danger">
                    {t('barcodeBulk.forceWarn', { defaultValue: 'Those are being labelled this type anyway — turn the box above back on to leave them alone.' })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={applying}>
            {tc('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={() => void apply()} loading={applying} disabled={busy || !preview || preview.matched === 0}>
            {t('barcodeBulk.apply', { defaultValue: 'Change {{n}} product(s)', n: preview?.matched ?? 0 })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
